import config from "../config";
import { refreshSession } from "./Auth";
import UserPool from "src/auth/UserPool";
import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
/**
 * Retrieves a valid Cognito Access Token, refreshing the session if expired.
 */

// ---- Config ----
const EXPIRY_BUFFER_MS = 60_000; // refresh if <= 60s left

// Centralized auto-logout (sign out + redirect back after login)
function autoLogout(reason: string): never {
  try {
    UserPool.getCurrentUser()?.signOut();
  } catch {}

  // You can customize this path:
  window.location.assign(`/login`);

  // Ensure caller flow stops immediately, even if navigation is blocked
  throw new Error(`AUTO_LOGOUT: ${reason}`);
}

/**
 * Returns a **valid** Cognito access token (JWT).
 * If we cannot obtain one (no user / invalid session / refresh failure), we **auto-logout**.
 */
export async function getCognitoAccessToken(): Promise<string> {
  const user = UserPool.getCurrentUser();
  if (!user) {
    throw new Error("No signed-in user found");
  }
  if (!user) autoLogout("No signed-in user found");

  // ---- Load current session (callback API -> Promise) ----
  const session: any = await new Promise((resolve, reject) => {
    user!.getSession((err: any, sess: unknown) =>
      err || !sess ? reject(err ?? new Error("No session")) : resolve(sess)
    );
  }).catch(() => autoLogout("Failed to load Cognito session"));

  if (session.isValid()) {
    return session.getAccessToken().getJwtToken();
  }

  // ---- If access token has comfortable runway, use it ----
  const access = session.getAccessToken();
  const expMs = access.getExpiration() * 1000;
  const now = Date.now();
  const msLeft = expMs - now;

  if (msLeft > EXPIRY_BUFFER_MS && session.isValid()) {
    return access.getJwtToken();
  }
  // ---- Session needs refresh: attempt refresh; if it fails -> auto-logout ----
  const refreshToken = session.getRefreshToken()?.getToken();
  const username = user!.getUsername();
  if (!refreshToken || !username) autoLogout("Missing refresh token or username");

  try {
    const { accessToken } = await refreshSession(refreshToken, username);
    if (!accessToken?.jwtToken) throw new Error("No jwtToken in refresh response");
    return accessToken.jwtToken;
  } catch (e) {
    autoLogout("Refresh failed");
  }
}

// Create Axios instance
const apiClient = axios.create({
  baseURL: `${config.API_BASE_HOST}/ui/v1`,
  withCredentials: true,
});
apiClient.defaults.headers.common.Accept = "application/json";

// Interceptor: inject Cognito Access Token into Authorization header
apiClient.interceptors.request.use(
  async (reqConfig: InternalAxiosRequestConfig) => {
    try {
      const token = await getCognitoAccessToken();
      reqConfig.headers = reqConfig.headers ?? {};
      reqConfig.headers.Authorization = `Bearer ${token}`;
    } catch (err) {
      console.error("Failed to attach Cognito Access Token:", err);
      // Optionally trigger sign-out or redirect
    }
    return reqConfig;
  },
  (error: AxiosError) => Promise.reject(error)
);

export default apiClient;
